<template>
  <a-modal
    :visible="visible"
    title="添加 Tab 到栏位"
    width="680px"
    ok-text="确认添加"
    cancel-text="取消"
    :body-style="{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }"
    :ok-button-props="{ disabled: confirmDisabled }"
    @ok="handleConfirm"
    @cancel="handleCancel"
  >
    <p class="modal-description">
      <template v-if="maxSelectable > 0">
        仅显示当前站点「已启用」且{{
          experiment ? '本变体' : '当前列表'
        }}未装配的物料，最多可选 {{ maxSelectable }} 个
      </template>
      <template v-else>当前已装配 5 个 Tab，无法继续添加</template>
    </p>

    <div v-if="candidates.length" class="candidate-list">
      <div
        v-for="item in candidates"
        :key="item.materialNo"
        class="candidate-row"
        :class="{ disabled: isCandidateDisabled(item) }"
      >
        <a-checkbox
          :checked="selectedMaterialNos.includes(item.materialNo)"
          :disabled="isCandidateDisabled(item)"
          @change="
            event => toggleCandidate(item.materialNo, event.target.checked)
          "
        />
        <div class="candidate-icon">
          <img
            v-if="getIconUrl(item)"
            :src="getIconUrl(item)"
            :alt="getMaterialName(item)"
          />
          <PictureOutlined v-else />
        </div>
        <div class="candidate-info">
          <strong>{{ getMaterialName(item) }}</strong>
          <span>
            {{ item.materialNo || '-' }} ·
            {{ item.materialTypeName || getMaterialTypeName(item) }} ·
            {{ getJumpTarget(item) }}
          </span>
        </div>
      </div>
    </div>
    <a-empty v-else description="暂无可添加的 Tab" />
  </a-modal>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { PictureOutlined } from '@ant-design/icons-vue'
import { getMaterialName as materialName } from '../configDraft'

const materialTypeNames = {
  1: 'icon',
  2: '大图',
  3: 'icon+文字'
}

export default {
  name: 'AddTabCandidatesModal',
  components: {
    PictureOutlined
  },
  props: {
    visible: Boolean,
    experiment: Boolean,
    defaultLanguageCode: { type: String, default: '' },
    candidates: {
      type: Array,
      default: () => []
    },
    maxSelectable: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:visible', 'confirm'],
  setup(props, { emit }) {
    const selectedMaterialNos = ref([])
    const confirmDisabled = computed(
      () => props.maxSelectable <= 0 || selectedMaterialNos.value.length === 0
    )

    const getMaterialName = item =>
      materialName(item, props.defaultLanguageCode)
    const getMaterialTypeName = item =>
      materialTypeNames[item.materialType] || '-'
    const getIconUrl = item =>
      item.unselectedIconUrl || item.selectedIconUrl || ''
    const getJumpTarget = item =>
      item.jumpType === 1
        ? item.h5Url || '-'
        : `路由 · ${item.routeName || item.routeUrl || item.routeId || '-'}`
    const isSelected = item =>
      selectedMaterialNos.value.includes(item.materialNo)
    const isCandidateDisabled = item =>
      !isSelected(item) &&
      (props.maxSelectable <= 0 ||
        selectedMaterialNos.value.length >= props.maxSelectable)
    const toggleCandidate = (materialNo, checked) => {
      if (checked) {
        if (
          props.maxSelectable <= 0 ||
          selectedMaterialNos.value.length >= props.maxSelectable
        ) {
          return
        }
        selectedMaterialNos.value = [...selectedMaterialNos.value, materialNo]
        return
      }
      selectedMaterialNos.value = selectedMaterialNos.value.filter(
        item => item !== materialNo
      )
    }
    const resetSelection = () => {
      selectedMaterialNos.value = []
    }
    const handleCancel = () => {
      resetSelection()
      emit('update:visible', false)
    }
    const handleConfirm = () => {
      if (confirmDisabled.value) return

      const selectedSet = new Set(selectedMaterialNos.value)
      emit(
        'confirm',
        props.candidates.filter(item => selectedSet.has(item.materialNo))
      )
    }

    watch(
      () => props.visible,
      () => resetSelection()
    )

    return {
      confirmDisabled,
      getIconUrl,
      getJumpTarget,
      getMaterialName,
      getMaterialTypeName,
      handleCancel,
      handleConfirm,
      isCandidateDisabled,
      selectedMaterialNos,
      toggleCandidate
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-description {
  margin: -4px 0 18px;
  color: #8c8c8c;
}

.candidate-list {
  border-top: 1px solid #f0f0f0;
}

.candidate-row {
  display: grid;
  grid-template-columns: 28px 52px minmax(0, 1fr);
  min-height: 84px;
  padding: 14px 8px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  column-gap: 14px;

  &.disabled {
    opacity: 0.55;
  }
}

.candidate-icon {
  display: flex;
  width: 52px;
  height: 52px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  background: #fafafa;
  align-items: center;
  justify-content: center;
  color: #bfbfbf;
  font-size: 22px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.candidate-info {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;

  strong {
    overflow: hidden;
    color: #262626;
    font-size: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    overflow: hidden;
    color: #8c8c8c;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
